import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  return (Math.round((num || 0) * 100) / 100).toLocaleString('cs-CZ');
}

/**
 * CallCenterCommissionsTab — "Callcentrum"
 * CC uživatelé generují vlastní provize (hodnota vygenerovaných leadů) + admin může přidat bonus.
 * Admin vidí všechny CC uživatele; CC uživatel vidí jen sebe.
 * Statistika s časovým rozpadem + generování faktury s QR kódem.
 */
export default function CallCenterCommissionsTab({ userRole, users, currentUserId, isDarkMode }) {
  const isAdmin = userRole === 'admin';
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(isAdmin ? 'all' : currentUserId);
  const [data, setData] = useState({ users: [], grandTotal: 0, totalValue: 0, totalBonus: 0, batchNames: [], batchTotals: {}, totalCount: 0 });
  const [loading, setLoading] = useState(false);

  // Bonus editor
  const [bonusEdit, setBonusEdit] = useState(null); // { userId, userName, amount, note }
  const [bonusSaving, setBonusSaving] = useState(false);

  // Faktura
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [invoiceUser, setInvoiceUser] = useState(null); // { userId, userName, total }
  const [invoiceData, setInvoiceData] = useState({
    supplierName: '', supplierAddress: '', supplierIco: '', supplierDic: '',
    accountNumber: '', variableSymbol: '', isVatPayer: false
  });

  const availableUsers = isAdmin ? (users || []).filter(u => u.role === 'callcentrum') : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/callcenter-commissions?month=${selectedMonth}&userId=${currentUserId || ''}`;
      const effectiveUser = isAdmin ? selectedUser : currentUserId;
      if (effectiveUser && effectiveUser !== 'all') url += `&filterUserId=${effectiveUser}`;
      const res = await fetch(url);
      const json = res.ok ? await res.json() : { users: [], grandTotal: 0, totalValue: 0, totalBonus: 0, batchNames: [], batchTotals: {}, totalCount: 0 };
      setData(json);
    } catch {
      setData({ users: [], grandTotal: 0, totalValue: 0, totalBonus: 0, batchNames: [], batchTotals: {}, totalCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, currentUserId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const saved = localStorage.getItem('invoiceData');
    if (saved) {
      try { setInvoiceData(prev => ({ ...prev, ...JSON.parse(saved) })); } catch { /* ignore */ }
    }
  }, []);

  const handleSaveBonus = async () => {
    if (!bonusEdit) return;
    setBonusSaving(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const res = await fetch(`${API_URL}/callcenter-bonus?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: bonusEdit.userId,
          month: parseInt(month),
          year: parseInt(year),
          amount: Number(bonusEdit.amount) || 0,
          note: bonusEdit.note || '',
          actorUserId: currentUserId
        })
      });
      if (res.ok) {
        setBonusEdit(null);
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při ukládání bonusu');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setBonusSaving(false);
    }
  };

  const saveInvoiceData = () => {
    localStorage.setItem('invoiceData', JSON.stringify(invoiceData));
    alert('Fakturační údaje uloženy');
  };

  const buildIban = (accountInput) => {
    let acc = (accountInput || '').trim().replace(/\s/g, '');
    if (acc.startsWith('CZ')) return acc;
    const parts = acc.split('/');
    if (parts.length !== 2) return acc;
    const bankCode = parts[1].padStart(4, '0');
    const accountParts = parts[0].split('-');
    let prefix = '000000';
    let accountNumber = '';
    if (accountParts.length === 2) {
      prefix = accountParts[0].padStart(6, '0');
      accountNumber = accountParts[1].padStart(10, '0');
    } else {
      accountNumber = accountParts[0].padStart(10, '0');
    }
    const bban = bankCode + prefix + accountNumber;
    const ibanCheck = bban + '123500';
    let remainder = 0;
    for (let i = 0; i < ibanCheck.length; i++) {
      remainder = (remainder * 10 + parseInt(ibanCheck[i])) % 97;
    }
    const checksum = (98 - remainder).toString().padStart(2, '0');
    return 'CZ' + checksum + bban;
  };

  const generateQRCode = (payoutAmount) => {
    if (!invoiceData.accountNumber || !payoutAmount) return '';
    const baseAmount = Math.round(payoutAmount * 100) / 100;
    const vatAmount = invoiceData.isVatPayer ? Math.round(baseAmount * 0.21 * 100) / 100 : 0;
    const qrAmount = invoiceData.isVatPayer ? Math.round((baseAmount + vatAmount) * 100) / 100 : baseAmount;
    const iban = buildIban(invoiceData.accountNumber);
    const amount = qrAmount.toFixed(2);
    const vs = invoiceData.variableSymbol || '';
    const qrData = `SPD*1.0*ACC:${iban}*AM:${amount}*CC:CZK*X-VS:${vs}*`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`;
  };

  const generateInvoicePDF = () => {
    if (!invoiceUser) return;
    const formatCurrency = (num) => num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const [year, month] = selectedMonth.split('-');
    const monthName = new Date(year, month - 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    const payoutAmount = invoiceUser.total;
    const baseAmount = Math.round(payoutAmount * 100) / 100;
    const vatAmount = invoiceData.isVatPayer ? Math.round(baseAmount * 0.21 * 100) / 100 : 0;
    const totalAmount = Math.round((baseAmount + vatAmount) * 100) / 100;
    const duzpDate = new Date(Number(year), Number(month), 0).toLocaleDateString('cs-CZ');

    const invoiceHTML = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Faktura - Callcentrum ${monthName}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 10px; }
            .header { text-align: center; margin-bottom: 15px; }
            .header h1 { font-size: 20px; margin: 5px 0; }
            .header p { font-size: 9px; margin: 2px 0; }
            .company-info { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 9px; }
            .company-info div { width: 48%; }
            .company-info h3 { font-size: 11px; margin: 0 0 5px 0; }
            .company-info p { margin: 2px 0; line-height: 1.3; }
            .items { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 9px; }
            .items th { border: 1px solid #ddd; padding: 4px; background-color: #f2f2f2; text-align: left; font-size: 9px; }
            .items td { border: 1px solid #ddd; padding: 4px; }
            .total { text-align: right; font-weight: bold; font-size: 11px; margin: 10px 0; }
            .total-row { background-color: #f9f9f9; }
            .invoice-details { margin: 10px 0; font-size: 9px; }
            .qr-code { text-align: center; margin-top: 10px; }
            .qr-code p { font-size: 9px; margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FAKTURA</h1>
            <p>Číslo faktury: ${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}</p>
            <p>Datum vystavení: ${new Date().toLocaleDateString('cs-CZ')}</p>
            <p>Datum splatnosti: ${(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('cs-CZ'); })()}</p>
            ${invoiceData.isVatPayer ? `<p>DUZP: ${duzpDate}</p>` : ''}
          </div>
          <div class="company-info">
            <div>
              <h3>Dodavatel:</h3>
              <p><strong>${invoiceData.supplierName}</strong></p>
              <p>${invoiceData.supplierAddress}</p>
              <p>Číslo účtu: ${invoiceData.accountNumber}</p>
              <p>IČO: ${invoiceData.supplierIco}</p>
              ${invoiceData.supplierDic ? `<p>DIČ: ${invoiceData.supplierDic}</p>` : ''}
              <p>${invoiceData.isVatPayer ? '<strong>Plátce DPH</strong>' : '<strong>Neplátce DPH</strong>'}</p>
            </div>
            <div>
              <h3>Odběratel:</h3>
              <p><strong>Vyjednáme, s.r.o.</strong></p>
              <p>Příčná 1892/4, Nové Město (Praha 1)</p>
              <p>110 00 Praha</p>
              <p>IČO: 21377596</p>
              <p>DIČ: CZ21377596</p>
            </div>
          </div>
          <table class="items">
            <thead>
              <tr>
                <th style="width: 80%;">Popis služby</th>
                <th style="width: 20%; text-align: right;">Částka</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-size: 10px; padding: 6px;">Provoz call centra a generování leadů - měsíc ${monthName}</td>
                <td style="font-size: 10px; padding: 6px; text-align: right;">${formatCurrency(baseAmount)} Kč</td>
              </tr>
              ${invoiceData.isVatPayer ? `
              <tr class="total-row">
                <td style="text-align: right; font-weight: bold; font-size: 10px; padding: 6px;">DPH 21%:</td>
                <td style="text-align: right; font-weight: bold; font-size: 10px; padding: 6px;">${formatCurrency(vatAmount)} Kč</td>
              </tr>` : ''}
              <tr class="total-row">
                <td style="text-align: right; font-weight: bold; font-size: 11px; padding: 6px;">Celkem ${invoiceData.isVatPayer ? 's DPH' : ''}:</td>
                <td style="text-align: right; font-weight: bold; font-size: 11px; padding: 6px;">${formatCurrency(invoiceData.isVatPayer ? totalAmount : baseAmount)} Kč</td>
              </tr>
            </tbody>
          </table>
          <div class="total">
            <p>Celková částka k úhradě: <strong>${formatCurrency(invoiceData.isVatPayer ? totalAmount : baseAmount)} Kč</strong></p>
          </div>
          <div class="invoice-details">
            <p><strong>Platební údaje:</strong></p>
            <p>Číslo účtu: ${invoiceData.accountNumber}</p>
            <p>Variabilní symbol: ${invoiceData.variableSymbol}</p>
          </div>
          <div class="qr-code">
            <p><strong>QR kód pro platbu:</strong></p>
            <img src="${generateQRCode(payoutAmount)}" alt="QR kód pro platbu" />
          </div>
        </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const openInvoice = (u) => {
    setInvoiceUser({ userId: u.userId, userName: u.userName, total: u.total });
    setShowInvoiceGenerator(true);
  };

  const th = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const td = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`;
  const tdMuted = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const card = `rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`;
  const inputCls = `w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div>
      {/* Filtry */}
      <div className={`flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div>
          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
        </div>
        {isAdmin && (
          <div>
            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Uživatel</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="all">— Všichni —</option>
              {availableUsers.map(u => (
                <option key={u._id} value={u._id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám…</div>
      ) : data.users.length === 0 ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné provize callcentra pro tento měsíc.</div>
      ) : (
        <div className="space-y-6">
          {/* Souhrn (admin: všichni) */}
          {isAdmin && data.users.length > 1 && (
            <div className={card}>
              <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📞 Souhrn provizí callcentra</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Uživatel</th>
                      <th className={th}>Počet leadů</th>
                      <th className={th}>Hodnota leadů</th>
                      <th className={th}>Bonus</th>
                      <th className={th}>Vyrovnání</th>
                      <th className={th}>Celkem</th>
                      <th className={th}>Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map(u => (
                      <tr key={u.userId} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`${td} font-medium`}>{u.userName}</td>
                        <td className={td}>{u.count}</td>
                        <td className={td}>{formatNumber(u.totalValue)} Kč</td>
                        <td className={td}>{u.bonus ? `${formatNumber(u.bonus)} Kč` : '—'}</td>
                        <td className={`${td} ${u.settlementAdjustment < 0 ? 'text-red-500 font-semibold' : ''}`}>{u.settlementAdjustment ? `${formatNumber(u.settlementAdjustment)} Kč` : '—'}</td>
                        <td className={`${td} font-bold text-green-500`}>{formatNumber(u.total)} Kč</td>
                        <td className="px-3 py-2 text-sm">
                          <div className="flex gap-1">
                            <button
                              onClick={() => setBonusEdit({ userId: u.userId, userName: u.userName, amount: u.bonus || 0, note: u.bonusNote || '' })}
                              className="text-xs px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white"
                            >Bonus</button>
                            <button
                              onClick={() => openInvoice(u)}
                              className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
                            >Faktura</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                      <td className={`${td} font-bold`}>Celkem</td>
                      <td className={`${td} font-bold`}>{data.totalCount || 0}</td>
                      <td className={`${td} font-bold`}>{formatNumber(data.totalValue)} Kč</td>
                      <td className={`${td} font-bold`}>{formatNumber(data.totalBonus)} Kč</td>
                      <td className={`${td} font-bold ${(data.totalSettlement || 0) < 0 ? 'text-red-500' : ''}`}>{data.totalSettlement ? `${formatNumber(data.totalSettlement)} Kč` : '—'}</td>
                      <td className={`${td} font-bold text-green-500`}>{formatNumber(data.grandTotal)} Kč</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Souhrn počtu leadů podle šarže (kolik leadů kdo udělal) */}
          {(data.batchNames || []).length > 0 && (
            <div className={card}>
              <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📊 Počet leadů podle šarže</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Uživatel</th>
                      {data.batchNames.map(bn => (
                        <th key={bn} className={`${th} text-right`}>{bn}</th>
                      ))}
                      <th className={`${th} text-right`}>Celkem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map(u => (
                      <tr key={u.userId} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`${td} font-medium`}>{u.userName}</td>
                        {data.batchNames.map(bn => (
                          <td key={bn} className={`${td} text-right`}>{(u.batchCounts && u.batchCounts[bn]) || '—'}</td>
                        ))}
                        <td className={`${td} text-right font-bold`}>{u.count}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                      <td className={`${td} font-bold`}>Celkem</td>
                      {data.batchNames.map(bn => (
                        <td key={bn} className={`${td} text-right font-bold`}>{(data.batchTotals && data.batchTotals[bn]) || 0}</td>
                      ))}
                      <td className={`${td} text-right font-bold`}>{data.totalCount || 0}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Detail jednotlivých uživatelů */}
          {data.users.map(u => (
            <div key={u.userId} className={card}>
              <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
                <h3 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  👤 {u.userName}
                </h3>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Hodnota leadů: {formatNumber(u.totalValue)} Kč
                      {u.bonus > 0 && ` + bonus ${formatNumber(u.bonus)} Kč`}
                    </div>
                    <div className="text-lg font-bold text-green-500">{formatNumber(u.total)} Kč</div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => setBonusEdit({ userId: u.userId, userName: u.userName, amount: u.bonus || 0, note: u.bonusNote || '' })}
                      className="text-xs px-3 py-1.5 rounded bg-yellow-500 hover:bg-yellow-600 text-white"
                    >+ Bonus</button>
                  )}
                  <button
                    onClick={() => openInvoice(u)}
                    className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                  >📄 Faktura</button>
                </div>
              </div>
              {u.bonusNote && (
                <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bonus – poznámka: {u.bonusNote}</p>
              )}
              {(u.settlementItems || []).length > 0 && (
                <div className={`mb-3 p-3 rounded ${isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                    ↩️ Stržení z uznaných reklamací (vyrovnání tento měsíc):
                  </p>
                  {u.settlementItems.map((s, i) => (
                    <div key={i} className={`text-xs flex justify-between ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span>{s.companyName || '—'}{s.batchName ? ` · ${s.batchName}` : ''}</span>
                      <span className="font-semibold text-red-500">{formatNumber(s.credit)} Kč</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Datum a čas</th>
                      <th className={th}>Zákazník</th>
                      <th className={th}>Šarže</th>
                      <th className={th}>Komu přiděleno</th>
                      <th className={th}>Hodnota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.leads.map((lead, idx) => (
                      <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-600' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={tdMuted}>{lead.createdAt ? new Date(lead.createdAt).toLocaleString('cs-CZ') : '—'}</td>
                        <td className={`${td} font-medium`}>{lead.companyName || '—'}</td>
                        <td className={`${tdMuted} max-w-[180px] truncate`} title={lead.buttonDescription || lead.batchName}>{lead.buttonDescription || lead.batchName || '—'}</td>
                        <td className={tdMuted}>{lead.assignedTo || '—'}</td>
                        <td className={`${td} font-semibold`}>{formatNumber(lead.value)} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal editace bonusu */}
      {bonusEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-md w-full m-4 p-5`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Bonus pro {bonusEdit.userName}
            </h3>
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Částka (Kč)</label>
                <input
                  type="number"
                  value={bonusEdit.amount}
                  onChange={e => setBonusEdit({ ...bonusEdit, amount: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Poznámka</label>
                <input
                  type="text"
                  value={bonusEdit.note}
                  onChange={e => setBonusEdit({ ...bonusEdit, note: e.target.value })}
                  className={inputCls}
                  placeholder="Volitelné"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setBonusEdit(null)} className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>Zrušit</button>
              <button onClick={handleSaveBonus} disabled={bonusSaving} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {bonusSaving ? 'Ukládám…' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal generátoru faktury */}
      {showInvoiceGenerator && invoiceUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Generátor faktury – {invoiceUser.userName}</h2>
              <button onClick={() => setShowInvoiceGenerator(false)} className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>✕</button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>Název dodavatele *</label>
                  <input type="text" value={invoiceData.supplierName} onChange={e => setInvoiceData({ ...invoiceData, supplierName: e.target.value })} className={inputCls} placeholder="Jméno nebo název firmy" />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>Adresa dodavatele *</label>
                  <input type="text" value={invoiceData.supplierAddress} onChange={e => setInvoiceData({ ...invoiceData, supplierAddress: e.target.value })} className={inputCls} placeholder="Adresa" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>IČO *</label>
                    <input type="text" value={invoiceData.supplierIco} onChange={e => setInvoiceData({ ...invoiceData, supplierIco: e.target.value })} className={inputCls} placeholder="IČO" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>DIČ</label>
                    <input type="text" value={invoiceData.supplierDic} onChange={e => setInvoiceData({ ...invoiceData, supplierDic: e.target.value })} className={inputCls} placeholder="DIČ (volitelné)" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>Číslo účtu *</label>
                    <input type="text" value={invoiceData.accountNumber} onChange={e => setInvoiceData({ ...invoiceData, accountNumber: e.target.value })} className={inputCls} placeholder="Číslo účtu" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>Variabilní symbol</label>
                    <input type="text" value={invoiceData.variableSymbol} onChange={e => setInvoiceData({ ...invoiceData, variableSymbol: e.target.value })} className={inputCls} placeholder="Variabilní symbol" />
                  </div>
                </div>
                <div>
                  <label className={`flex items-center space-x-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={invoiceData.isVatPayer} onChange={e => setInvoiceData({ ...invoiceData, isVatPayer: e.target.checked })} className="rounded" />
                    <span className="text-sm font-medium">Jsem plátce DPH</span>
                  </label>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Náhled faktury:</h4>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Měsíc:</strong> {(() => { const [y, m] = selectedMonth.split('-'); return new Date(y, m - 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' }); })()}</p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Částka k výplatě (bez DPH):</strong> {formatNumber(invoiceUser.total)} Kč</p>
                  {invoiceData.isVatPayer && (
                    <>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>DPH 21%:</strong> {formatNumber(invoiceUser.total * 0.21)} Kč</p>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Celkem s DPH:</strong> {formatNumber(invoiceUser.total * 1.21)} Kč</p>
                    </>
                  )}
                </div>
                <div className="flex space-x-4">
                  <button onClick={saveInvoiceData} disabled={!invoiceData.supplierName || !invoiceData.supplierAddress || !invoiceData.supplierIco || !invoiceData.accountNumber} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50">💾 Uložit údaje</button>
                  <button onClick={generateInvoicePDF} disabled={!invoiceData.supplierName || !invoiceData.accountNumber} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">📄 Vygenerovat fakturu</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
